<script setup lang="ts">
import type { StyleSpecification } from 'maplibre-gl'
import { MglMap, MglMarker } from '@indoorequal/vue-maplibre-gl'
import { colord, extend } from 'colord'
import mixPlugin from 'colord/plugins/mix'
import { setWorkerUrl } from 'maplibre-gl'
// maplibre resolves its worker via import.meta.url, which breaks once bundled: let Vite bundle it
import workerUrl from 'maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url'
import 'maplibre-gl/dist/maplibre-gl.css'

interface BrandMapProps {
  lat: number
  lng: number
  zoom?: number | null
  foreground: string
  background: string
}

const props = defineProps<BrandMapProps>()

const emit = defineEmits<{
  markerClick: []
}>()

extend([mixPlugin])

// OpenFreeMap: free, no API key, commercial use allowed
const STYLE_URL = 'https://tiles.openfreemap.org/styles/positron'

// Darkens the map base slightly, so it stands apart from the section background
const BASE_TINT = 0.06

// How much of the foreground color is mixed into the background, per layer
function layerTint(id: string, type: string) {
  if (type === 'fill') {
    if (id === 'water')
      return 0.2
    if (id === 'building')
      return 0.1
    if (id === 'park' || id === 'landcover_wood')
      return 0.08
    return 0.04
  }
  if (type === 'line') {
    if (id.startsWith('boundary'))
      return 0.35
    if (id === 'waterway')
      return 0.2
    if (id.includes('casing'))
      return 0.22
    return 0.14
  }
  return 0.8
}

interface MapColors {
  fg: string
  bg: string
}

function recolorStyle(style: StyleSpecification, { fg, bg }: MapColors): StyleSpecification {
  const tint = (amount: number) => colord(bg).mix(fg, amount).toHex()

  for (const layer of style.layers) {
    const paint: Record<string, unknown> = ((layer as { paint?: object }).paint ??= {})
    switch (layer.type) {
      case 'background':
        paint['background-color'] = bg
        break
      case 'fill':
        paint['fill-color'] = tint(layerTint(layer.id, layer.type))
        if (layer.id === 'building')
          paint['fill-outline-color'] = tint(0.16)
        break
      case 'line':
        paint['line-color'] = tint(layerTint(layer.id, layer.type))
        break
      case 'symbol':
        // Road shields are images: they can't be recolored
        if (layer.id.includes('shield')) {
          layer.layout = { ...layer.layout, visibility: 'none' }
          break
        }
        paint['text-color'] = tint(layerTint(layer.id, layer.type))
        paint['text-halo-color'] = bg
        break
    }
  }
  return style
}

const colors = computed<MapColors>(() => ({
  fg: props.foreground,
  bg: colord(props.background).mix(props.foreground, BASE_TINT).toHex(),
}))

const baseStyle = shallowRef<StyleSpecification>()
const isLoaded = ref(false)

onMounted(async () => {
  setWorkerUrl(workerUrl)
  baseStyle.value = await $fetch<StyleSpecification>(STYLE_URL)
})

const mapStyle = computed(() => {
  if (!baseStyle.value)
    return undefined
  return recolorStyle(structuredClone(baseStyle.value), colors.value)
})
</script>

<template>
  <div class="brand-map" :style="{ backgroundColor: colors.bg }">
    <ClientOnly>
      <div
        v-if="mapStyle"
        class="motion-relaxed size-full"
        :class="{ 'opacity-0': !isLoaded, 'opacity-100': isLoaded }"
      >
        <MglMap
          :map-style="mapStyle"
          :center="[lng, lat]"
          :zoom="zoom ?? 15"
          :min-zoom="3"
          :scroll-zoom="false"
          :drag-rotate="false"
          :pitch-with-rotate="false"
          :attribution-control="{ compact: true }"
          @map:load="isLoaded = true"
        >
          <MglMarker :coordinates="[lng, lat]">
            <template #marker>
              <div class="size-8 cursor-pointer" @click="emit('markerClick')">
                <slot name="marker" />
              </div>
            </template>
          </MglMarker>
        </MglMap>
      </div>
    </ClientOnly>
  </div>
</template>

<style scoped>
.brand-map {
  background-color: var(--color-scheme-background);
}

.brand-map :deep(.maplibregl-ctrl-attrib) {
  background-color: var(--color-scheme-background);
  color: var(--color-scheme-foreground);
}

.brand-map :deep(.maplibregl-ctrl-attrib a) {
  color: inherit;
}
</style>
